import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Cookies } from 'react-cookie';
import {
  Box,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Button,
  IconButton,
  Grid,
  Divider,
  Tooltip,
} from '@mui/material';
import { DefaultUserIconFilled, MenuIcon } from 'components/SVG';
import { useWorkspaces } from 'hooks/Workspace/useWorkSpaces';
import { useWorkspaceContext } from 'hooks/common/useWorkspaceContext';
import { useUpdateApplicationUser } from 'hooks/ApplicationUser';
import { useCurrentUser } from 'hooks/common/useCurrentUser';
import { useResize } from 'hooks/common';
import { useEnvironment } from 'hooks/useEnvironment';
import { SPARK_ROUTES } from 'constants/routes';
import { SparkButton, SparkCircularProgress } from 'sharedComponents/UI';
import { SparkImageLoader } from 'sharedComponents/UI';
import WorkspaceMenu from './WorkspaceMenu';

type HeaderProps = {
  toggleDrawer: () => void;
};

const Header = React.forwardRef(function Header(
  { toggleDrawer }: HeaderProps,
  ref
) {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getWorkspace } = useWorkspaceContext();
  const { isMobile } = useResize();
  const { isProduction } = useEnvironment();
  const { currentUserID, applicationUser } = useCurrentUser();
  const { mutateAsync: updateUser } = useUpdateApplicationUser(currentUserID);
  const { dropdownOptions: workspacesDropdownOptions } = useWorkspaces();
  const workSpace = getWorkspace();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    cookies.remove('userToken', { path: '/' });
    cookies.remove('userID', { path: '/' });
    queryClient.clear();
    navigate(SPARK_ROUTES.LOGIN);
  };
  const handleWorkspaceSelect = (workspaceID: string) => {
    handleClose();
    updateUser({ defaultWorkspaceID: workspaceID });
  };
  const circularProgressValues = workSpace?.meta?.recommendedMemories;

  return (
    <Grid ref={ref as any} container height='inherit'>
      <Toolbar disableGutters sx={{ width: '100%' }}>
        {isMobile && !isProduction && (
          <Box paddingLeft={1.5}>
            <IconButton onClick={toggleDrawer}>
              <MenuIcon />
            </IconButton>
          </Box>
        )}
        <WorkspaceMenu
          workspaceOptions={workspacesDropdownOptions}
          handleWorkspaceSelect={handleWorkspaceSelect}
          workSpace={workSpace}
          isProduction={isProduction}
        />
        <Tooltip
          title={workSpace?.attributes?.name + ' Knowledge'}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -14],
                  },
                },
              ],
            },
          }}
        >
          <div
            onClick={() => {
              navigate(`/settings/workspaces/${workSpace?.id}`, {
                state: { selectedTabIndex: 1 },
              });
            }}
          >
            <SparkCircularProgress
              value={circularProgressValues?.percentageComplete || 0}
              color={circularProgressValues?.percentageCompleteColour || ''}
            />
          </div>
        </Tooltip>
        <Box justifyContent='flex-end' sx={{ flexGrow: 1, display: 'flex' }}>
          <Button onClick={handleClick}>
            <Box
              justifyContent='flex-end'
              sx={{ flexGrow: 1, display: 'flex' }}
            >
              {currentUserID &&
              applicationUser?.attributes?.profilePictureURL ? (
                <SparkImageLoader
                  src={applicationUser?.attributes?.profilePictureURL || ''}
                  alt={applicationUser?.attributes?.profilePictureURL || ''}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                  }}
                />
              ) : (
                <DefaultUserIconFilled width='40' height='40' />
              )}
            </Box>
          </Button>

          <Menu
            open={open}
            onClose={handleClose}
            anchorEl={anchorEl}
            PaperProps={{
              sx: {
                width: '300px',
              },
            }}
          >
            <Box sx={{ padding: 2, textAlign: 'left' }}>
              <Typography variant='h5'>
                {applicationUser?.attributes?.name || ''}
              </Typography>
              <Typography variant='body1' color='textSecondary'>
                {applicationUser?.attributes?.email || ''}
              </Typography>
            </Box>
            <Divider />
            <MenuItem sx={{ mt: 1 }}>
              <SparkButton
                variant='contained'
                sx={{ borderRadius: '2px', width: '100%' }}
                onClick={() => {
                  navigate('/settings/billing-and-usage?subscriptionPopup=1');
                }}
              >
                Try Pro
              </SparkButton>
            </MenuItem>
            <Divider />
            {!isProduction && (
              <MenuItem
                onClick={() => {
                  navigate('/settings/my-profile');
                }}
                sx={{ mt: 1 }}
              >
                My Profile
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </Grid>
  );
});

export default Header;
